import React, { useEffect, useRef, useState } from "react";
import { fetcher } from "../../../services/api";
import CheckerEye from "../../../assets/icons/checker-eye";
import CheckerEyeUnlocked from "../../../assets/icons/checker-eye-unlocked";
import CopyIcon from "../../../assets/icons/copy";
import ChevronDownIcon from "../../../assets/icons/chveron-down";
import AlertIcon from "../../../assets/icons/alert";
import ApiNoContent from "../ApiNoContent";
import Loader from "../../Loader/Loader";
import AppButton from "../../AppButton";
import AddPortalModal from "../AddPlatfromModal";
import AdsLinesModal from "../AdsLinesModal";
import PlatformDeleteModal from "../PlatformDeleteModal";
import ApiKeysDotsModal from "../ApiKeysDotsModal";
import RequestProductionModal from "../RequestProductionModal";
import CreateKeyModal from "../CreateKeyModal";
import "./ApiKeysTable.css";
import HeartIcon from "../../../assets/icons/heart";
import CheckIcon from "../../../assets/icons/check";

interface ApiKeysTableProps {
  setHasData: (hasData: boolean) => void;
  shouldRefetch?: boolean;
  onPlatformCreate?: () => void;
  onPlatformDelete?: () => void;
  onRequestProduction?: () => void;
}

export interface AppData {
  group_key: string;
  ads_enabled: boolean;
  ad_store_url: string;
  ad_lines_is_valid: boolean;
  key: string;
  secret: string;
  display_name: string;
  status: string;
  created_at: string;
  groupName: string;
  created_at_app: string;
  groupDomain: string;
  ad_bundle: string;
  ad_app_name: string;
  advertisement_position: number | null;
}

export interface PlatformData {
  key: string;
  name: string;
  email: string;
  description: string;
  key_usage_description: string;
  domain: string;
  agreed_to_terms: boolean;
}

interface GroupedDataItem {
  groupName: string;
  groupKey: string;
  created_at_app: string;
  groupDomain: string;
  groupMail: string;
  groupDesc: string;
  addLinesIsValid: boolean;
  apps: AppData[];
}

const ApiKeysTable: React.FC<ApiKeysTableProps> = ({
  setHasData,
  shouldRefetch,
  onPlatformCreate,
  onPlatformDelete,
  onRequestProduction,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditPlatformModalOpen, setIsEditPlatformModalOpen] = useState(false);
  const [isAdsModalOpen, setIsAdsModalOpen] = useState(false);
  const [appData, setAppData] = useState<AppData[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroupKey, setSelectedGroupKey] = useState<string | null>(null);
  const [visibleSecrets, setVisibleSecrets] = useState<Record<string, boolean>>(
    {}
  );
  const [groupedData, setGroupedData] = useState<
    Record<string, GroupedDataItem>
  >({});
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
  const [selectedClientKey, setSelectedClientKey] = useState<string | null>(
    null
  );
  const groupRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [selectedAppKey, setSelectedAppKey] = useState<string | null>(null);
  const [isRequestModalVisible, setIsRequestModalVisible] = useState(false);
  const [requestProductionPrefill, setRequestProductionPrefill] = useState<{
    appName: string;
    ad_store_url: string;
    clientKey: string;
  } | null>(null);
  const [editAppData, setEditAppData] = useState<AppData | null>(null);
  const [editPlatformData, setEditPlatformData] = useState<PlatformData | null>(
    null
  );

  const handleDeleteClick = (key: string) => {
    setSelectedGroupKey(null);
    setSelectedAppKey(key);
    setIsDeleteModalVisible(true);
  };

  const handlePlatformDeleteClick = (key: string) => {
    setSelectedAppKey(null);
    setSelectedGroupKey(key);
    setIsDeleteModalVisible(true);
  };

  const handleConfirmDelete = async (key: string | null) => {
    if (!key) return;
    try {
      await fetcher(`/publisher/client/${key}/delete`, {
        method: "POST",
      });
      setAppData((prev) => prev.filter((item) => item.key !== key));
      onPlatformDelete?.();
    } catch (err) {
      console.error("Failed to delete app:", err);
    }
  };

  const handlePlatformConfirmDelete = async (key: string | null) => {
    if (!key) return;
    try {
      await fetcher(`/publisher/client/group/${key}/delete`, {
        method: "POST",
      });
      setAppData((prev) => prev.filter((item) => item.key !== key));
      setSelectedGroupKey(null);
      onPlatformDelete?.();
    } catch (err) {
      console.error("Failed to delete platform:", err);
    }
  };

  const toggleSecretVisibility = (key: string) => {
    setVisibleSecrets((prev) => {
      const newState = { ...prev, [key]: !prev[key] };
      if (!prev[key]) {
        setTimeout(() => {
          const input = inputRefs.current[key];
          if (input) {
            input.focus();
            input.select();
          }
        }, 0);
      }
      return newState;
    });
  };

  const copyToClipboard = async (secret: string, key: string) => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1500);
    } catch (err) {
      console.error("Copy failed", err);
    }
  };

  const toggleGroup = (groupKey: string) => {
    setOpenGroups((prev) => {
      const updated = new Set(prev);
      updated.has(groupKey)
        ? updated.delete(groupKey)
        : updated.add(groupKey);
      return updated;
    });
  };

  const handleEditClick = (key: string) => {
    const selectedApp = appData.find((app) => app.key === key);
    if (selectedApp) {
      setEditAppData(selectedApp);
      setIsModalOpen(true);
    }
  };

  const handlePlatformEditClick = (key: string) => {
    const group = Object.values(groupedData).find((g) => g.groupKey === key);
    if (group) {
      setSelectedGroupKey(key);
      setIsEditPlatformModalOpen(true);
      setEditPlatformData({
        key: group.groupKey,
        name: group.groupName,
        email: group.groupMail,
        description: group.groupDesc,
        key_usage_description: group.groupDesc,
        domain: group.groupDomain,
        agreed_to_terms: false,
      });
    }
  };

  const handleRequestProductionClick = (
    appName: string,
    ad_store_url: string,
    clientKey: string
  ) => {
    setRequestProductionPrefill({ appName, ad_store_url, clientKey });
    setIsRequestModalVisible(true);
  };

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const res = (await fetcher("/publisher/client", {
          method: "GET",
        })) as { data: { data: { data: any[] } } };

        const apps = res.data.data.data;

        const groupMap: Record<string, GroupedDataItem> = {};

        apps.forEach((app: any) => {
          const groupName = app.name;
          const groupKey = app.key;
          const created_at_app = app.created_at;
          const groupDomain = app.domain;
          const groupMail = app.mail;
          const groupDesc = app.description;
          const addLinesIsValid = app.ad_lines_is_valid;
          const clients = Array.isArray(app.clients) ? app.clients : [];

          groupMap[groupKey] = {
            groupName,
            groupKey,
            created_at_app,
            groupDomain,
            groupMail,
            groupDesc,
            addLinesIsValid,
            apps: clients.map((client: any) => ({
              key: client.key,
              secret: client.secret,
              display_name: client.display_name,
              status: client.status,
              created_at: client.created_at,
              groupName,
              created_at_app,
              groupKey,
              groupDomain,
              advertisement_position: client.advertisement_position,
              ad_bundle: client.ad_bundle,
              ad_app_name: client.ad_app_name,
              ad_store_url: client.ad_store_url,
              ad_lines_is_valid: client.ad_lines_is_valid,
              ads_enabled: client.ads_enabled,
            })),
          };
        });

        setGroupedData(groupMap);
        const allClients = Object.values(groupMap).flatMap((g) => g.apps);
        setAppData(allClients);
        setHasData(allClients.length > 0);

        if (selectedGroupKey) {
          const groupExists = Object.values(groupMap).find(
            (g) => g.groupKey === selectedGroupKey
          );

          if (groupExists) {
            setTimeout(() => {
              setOpenGroups(new Set([selectedGroupKey]));
            }, 50);
          } else {
            setSelectedGroupKey(null);
            if (Object.keys(groupMap).length > 0) {
              setTimeout(() => {
                setOpenGroups(new Set([Object.values(groupMap)[0].groupKey]));
              }, 50);
            }
          }
        } else {
          if (Object.keys(groupMap).length > 0) {
            setTimeout(() => {
              setOpenGroups(new Set([Object.values(groupMap)[0].groupKey]));
            }, 50);
          }
        }
      } catch (err) {
        console.error("Failed to fetch analytics:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchClients();
  }, [selectedGroupKey, setHasData, shouldRefetch]);

  const handleUpdateAppIsValidInListing = (key: string, ad_lines_is_valid: boolean) => {
    let newGroupedData = {...groupedData}
    try {
      newGroupedData[key].addLinesIsValid = ad_lines_is_valid
      setGroupedData(newGroupedData)
    } catch (e) {
    }
  }

  return (
    <div className="app-analytics">
      {loading ? (
        <Loader />
      ) : Object.keys(groupedData).length === 0 ? (
          <ApiNoContent
          icon={<HeartIcon />}
          iconClassName="api-keys__heart-icon"
          triggerRefetch={onPlatformCreate}
          title="No Platform Added Yet"
          description="Let's get you started! Click the button to generate your first API key."
          buttonText="Add Platform"
          onButtonClick={() => ""}
        />
      ) : (
        <div className="app-api-keys-content">
          <div className="main-titles-of-tables">
            <span>Platform Name</span>
            <span>Monetization</span>
            <span className="right-content-for-main-title">Creation Date</span>
          </div>
          {Object.values(groupedData).map(({ groupName, groupKey, addLinesIsValid, apps }) => {
            const isOpen = openGroups.has(groupKey);
            return (
              <div
                className="group-of-company"
                style={{
                  borderBottom: !isOpen
                    ? "1px solid var(--color-dark-400)"
                    : "none",
                }}
                key={groupKey}
              >
                <div
                  className="accordion-header"
                  onClick={() => toggleGroup(groupKey)}
                >
                  <h3>{groupName}</h3>

                  <div className="monetization">
                    {apps.some((app) => app.ads_enabled) ? (
                      <>
                        <span>ON</span>
                        <p
                            className={`update-required ${addLinesIsValid ? 'green' : 'red'}`}
                            onClick={() => {
                            setSelectedClientKey(groupKey);
                            setIsAdsModalOpen(true);
                            setOpenGroups((prev) => {
                              const updated = new Set(prev);
                              updated.delete(groupKey);
                              return updated;
                            });
                          }}
                        >
                          {addLinesIsValid ? <CheckIcon /> : <AlertIcon />}
                          Ads Lines
                        </p>

                      </>
                    ) : (
                      <span>OFF</span>
                    )}
                  </div>

                  <div className="date-and-arrow">
                    <span>
                      {new Date(groupedData[groupKey]?.created_at_app).toLocaleDateString()}
                    </span>
                    <span className="arrow-and-dots">
                      <ChevronDownIcon
                        style={{
                          transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                          transition: "transform 0.3s ease",
                        }}
                      />
                      <span onClick={(e) => e.stopPropagation()}>
                        <ApiKeysDotsModal
                          onEditClick={handlePlatformEditClick}
                          onDeleteClick={handlePlatformDeleteClick}
                          appKey={groupKey}
                          platform
                        />
                      </span>
                    </span>
                  </div>
                </div>

                <div
                  ref={(el) => {
                    groupRefs.current[groupKey] = el;
                  }}
                  className={`inner-table-wrapper animated-wrapper ${
                    isOpen ? "open" : ""
                  }`}
                  style={{
                    maxHeight: isOpen
                      ? `${groupRefs.current[groupKey]?.scrollHeight}px`
                      : "",
                  }}
                >
                  <div className="inner-table">
                    {apps.length !== 0 && (
                      <table>
                        <thead>
                          <tr>
                            <th>APP Name</th>
                            <th>Key</th>
                            <th>Creation Date</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {apps.map((app) => (
                            <tr key={app.key}>
                              <td>{app.display_name || "N/A"}</td>
                              <td>
                                <div className="main-key-container">
                                  <button
                                    className="eye-button"
                                    onClick={() =>
                                      toggleSecretVisibility(app.key)
                                    }
                                  >
                                    {visibleSecrets[app.key] ? (
                                      <CheckerEyeUnlocked />
                                    ) : (
                                      <CheckerEye />
                                    )}
                                  </button>
                                  {visibleSecrets[app.key] ? (
                                    <div className="key-container">
                                      <div className="key-with-label">
                                        <label className="key-label">
                                          Your Key
                                        </label>
                                        <input
                                          type="text"
                                          value={app.secret}
                                          readOnly
                                          ref={(el) => {
                                            inputRefs.current[app.key] = el;
                                          }}
                                          className="key-input"
                                        />
                                      </div>
                                      <button
                                        onClick={() =>
                                          copyToClipboard(app.secret, app.key)
                                        }
                                      >
                                        {copiedKey === app.key ? (
                                          <span>Copied!</span>
                                        ) : (
                                          <CopyIcon />
                                        )}
                                      </button>
                                    </div>
                                  ) : (
                                    <button
                                      className="show-key-button"
                                      onClick={() =>
                                        toggleSecretVisibility(app.key)
                                      }
                                    >
                                      SHOW KEY
                                    </button>
                                  )}
                                </div>
                              </td>
                              <td>
                                {new Date(app.created_at).toLocaleDateString()}
                              </td>
                              <td>
                                <span
                                  className={`status-badge ${
                                    app.status === "PRODUCTION"
                                      ? "status-production"
                                      : app.status === "PENDING"
                                      ? "status-pending"
                                      : ""
                                  }`}
                                >
                                  {app.status}
                                </span>
                              </td>
                              <td>
                                <ApiKeysDotsModal
                                  onEditClick={handleEditClick}
                                  onDeleteClick={handleDeleteClick}
                                  appKey={app.key}
                                  onRequestProductionClick={() =>
                                    handleRequestProductionClick(
                                      app.display_name,
                                      app.ad_store_url,
                                      app.key
                                    )
                                  }
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}

                    <div className="add-platform-button">
                      <AppButton
                        variant="third"
                        onClick={() => {
                          setSelectedGroupKey(groupKey);
                          setIsModalOpen(true);
                          setEditAppData(null);
                        }}
                      >
                        Create Key
                      </AppButton>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <CreateKeyModal
        isVisible={isEditPlatformModalOpen}
        setIsModalVisible={setIsEditPlatformModalOpen}
        onKeyCreated={onPlatformCreate}
        editData={editPlatformData}
      />
      <AddPortalModal
        isVisible={isModalOpen}
        setIsModalVisible={setIsModalOpen}
        groupKey={selectedGroupKey}
        onPlatformCreate={onPlatformCreate}
        editData={editAppData}
      />
      <AdsLinesModal
        onUpdateAppIsValidInListing={handleUpdateAppIsValidInListing}
        isVisible={isAdsModalOpen}
        setIsVisible={setIsAdsModalOpen}
        clientKey={selectedClientKey || ""}
      />
      <PlatformDeleteModal
        isVisible={isDeleteModalVisible}
        setIsModalVisible={setIsDeleteModalVisible}
        onConfirmDelete={(key) =>
          selectedGroupKey
            ? handlePlatformConfirmDelete(selectedGroupKey)
            : handleConfirmDelete(key)
        }
        selectedCountry={selectedAppKey}
        platform={selectedGroupKey !== null}
      />
      <RequestProductionModal
        isVisible={isRequestModalVisible}
        setIsModalVisible={setIsRequestModalVisible}
        onClose={() => {
          setIsRequestModalVisible(false);
          setRequestProductionPrefill(null);
        }}
        clientKey={requestProductionPrefill?.clientKey || ""}
        prefillData={requestProductionPrefill}
        onRequestProduction={onRequestProduction ?? (() => {})}
      />
    </div>
  );
};

export default ApiKeysTable;
